/**********************************************************************
// @@@ START COPYRIGHT @@@
//        AVI SOLAR ENERGY CONFIDENTIAL: NEED TO KNOW ONLY
//        Copyright 2018 - 2019
//        Avi Solar Energy Pvt Ltd.
//        Protected as an unpublished work.
//  The computer program listings, specifications and documentation
//  herein are the property of Avi Solar Energy Pvt Ltd., or a third party supplier
//  and shall not be reproduced, copied, disclosed, or used in whole or in part for
//  any reason without the prior express written permission of Avi Solar Energy Pvt Ltd.
// @@@ END COPYRIGHT @@@
// +++ Code modified on 2019/02/25
**********************************************************************/
<template>
  <Card>
    <Toolbar>
      <v-flex sm2>
        <v-toolbar-title class="body-2">{{ $t('String Evaluation') }}</v-toolbar-title>
      </v-flex>
      <v-spacer></v-spacer>
      <v-flex sm4 text-xs-center v-if="stringData && stringData.length">
        <span class="mean-value">{{ $t('Ref String') }}: <span>{{ computeRefString }}</span></span>
        <span class="mean-value">{{ $t('Ref Value') }}: <span>{{ computeMean }}</span></span>
      </v-flex>
      <v-spacer></v-spacer>
      <v-flex sm2>
        <span></span>
      </v-flex>
    </Toolbar>

    <v-tabs v-if="tabs.length" v-model="activeTab" centered slider-color="accent" color="primary" grow class="br-4">
      <v-tab v-for="tab in tabs" :key="tab.value" ripple>
        {{ tab.name }}
      </v-tab>
    </v-tabs>

    <v-container>
      <v-layout row wrap>
        <v-flex sm12>
          <v-data-table :headers="headers" :items="stringData"
            hide-actions item-key="inverter_id" class="elevation-1 scba-fixed-header">
            <template slot="items" slot-scope="props">
              <tr>
                <td class="text-xs-center">{{ props.item.channel}}</td>
                <td class="text-xs-center">{{ props.item.current }}</td>
                <td class="text-xs-center">{{ props.item.deviation }}</td>
                <td>
                  <div class="color-coding-box" :class="props.item.evaluation"></div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>

        <v-flex sm12>
          <GlobalGraph class="graph" v-if="series.length" :series="series" :xAxis="xAxis" :yAxis="yAxis" :theme="theme" :loading="loading" />
        </v-flex>
      </v-layout>
    </v-container>
  </Card>
</template>

<script>
import { mapGettersMixin } from '@/util/mixin/index'
import GlobalGraph from '@/components/common/GlobalGraph.vue'
import service from '@/services/zone-monitoring.js'

import StringEvaluationApi from '@/services/StringEvaluation'
import {
  mapGetters,
  mapMutations
} from 'vuex'
const momentTz = require('moment-timezone')

export default {
  components: {
    GlobalGraph
  },
  mixins: [ mapGettersMixin ],
  data() {
    return {
      series: [],
      xAxis: [],
      idNames: ['B', 'R', 'INV', 'SMB', 'CH'],
      activeTab: 0,
      stringData: [],
      meanValue: null,
      refString: null,
      data: {},
      tabs: [],
      groupLength: null,
      loading: false
    }
  },
  computed: {
    ...mapGetters(['getSelectedPlantId', 'getActivePlantTimezone']),
    headers() {
      return [{
          text: this.$t('Channel'),
          align: 'center',
          sortable: false
        },
        {
          text: this.$t('Current (A)'),
          align: 'center',
          sortable: false
        },
        {
          text: this.$t('Deviation (%)'),
          align: 'center',
          sortable: false
        },
        {
          text: this.$t('Evaluation'),
          align: 'center',
          sortable: false
        }
      ]
    },
    yAxis() {
      return [
        {
          name: this.$t('Current (A)'),
          axisLabel: {
            formatter: '{value}',
            rotate: 40
          }
        }
      ]
    },
    watchDataCall() {
      return `${this.getSelectedPlantId} ${this.activeTab}`
    },
    computeRefString() {
      if (!this.refString) return '-'
      const idMap = {
        4: ['B', 'R', 'INV', 'CH'],
        5: ['B', 'R', 'INV', 'SMB', 'CH']
      }
      const ids = this.refString.split('-')
      const idArr = ids.slice(this.groupLength)
      const mapId = idMap[ids.length].slice(this.groupLength)

      return idArr.map((id, i) => `${mapId[i]}${id}`).join('-')
    },
    computeMean() {
      const value = parseFloat(this.meanValue)
      return isNaN(value) ? '-' : value.toFixed(2)
    }
  },
  async mounted() {
    await this.getStringData()
    this.loadStringData()
  },
  methods: {
    ...mapMutations([ 'SET_PROGRESS_BAR' ]),
    getParam() {
      return {
        plantId: this.getSelectedPlantId,
        dateTo: momentTz().tz(this.getActivePlantTimezone).format('YYYY-MM-DD'),
        dateFrom: momentTz().tz(this.getActivePlantTimezone).format('YYYY-MM-DD'),
        type: 'inverter',
        idList: this.tabs[this.activeTab].value
      }
    },
    async getStringData() {
      try {
        this.SET_PROGRESS_BAR(true)
        const resData = await StringEvaluationApi.getStringEvaluationData(this.getSelectedPlantId)
        this.data = resData.data.data.evaluationData
        this.tabs = Object.keys(this.data).map(id => {
          return {
            name: id.split('-').map((id, i) => `${this.idNames[i]}${id}`).join('-'),
            value: id
          }
        })
        this.SET_PROGRESS_BAR(false)
      } catch (err) {
        this.SET_PROGRESS_BAR(false)
        this.data = []
        this.tabs = []
      }

      try {
        this.loading = true
        let param = this.getParam()
        if (param) {
          this.series = []
          let response = await service.getStringDataForEvalPage(param)
          Object.keys(response.data.message.response).forEach(current => {
            this.series.push({
              name: current,
              type: 'line',
              data: response.data.message.response[current]
            })
          })
          this.xAxis = response.data.message.axis
          this.loading = false
        }
      } catch (err) {
        this.loading = false
        this.series = []
        this.xAxis = []
      }
    },
    loadStringData() {
      this.stringData = []
      this.meanValue = null
      this.refString = null
      this.groupLength = this.tabs.length && this.tabs[0].value.split('-').length
      const id = this.tabs[this.activeTab].value
      this.loadString(id)
    },
    loadString(activeTab) {
      const data = this.data[activeTab]
      if (!data) {
        this.meanValue = null
        this.refString = null
        this.stringData = []
        return
      }
      this.meanValue = data.mean
      this.refString = data.meanRefId
      const stringArr = data.stringEvaluationData
        .filter(({
          channelAvgCurrent
        }) => channelAvgCurrent !== 'NC')
        .map(({
          channelId,
          channelAvgCurrent,
          percentageDeviation
        }) => {
          return {
            channel: this.formatChannelId(channelId),
            current: this.formatValue(channelAvgCurrent),
            deviation: this.formatValue(percentageDeviation),
            evaluation: this.getColorCode(percentageDeviation)
          }
        })
      this.stringData = stringArr
    },
    formatChannelId(id) {
      if (!id) return '-'
      const idMap = {
        4: ['B', 'R', 'INV', 'CH'],
        5: ['B', 'R', 'INV', 'SMB', 'CH']
      }
      const ids = id.split('-')
      const idArr = ids.slice(this.groupLength)
      const mapId = idMap[ids.length].slice(this.groupLength)

      return idArr.map((id, i) => `${mapId[i]}${id}`).join('-')
    },
    formatValue(val) {
      const value = parseFloat(val)
      return isNaN(value) ? '-' : value.toFixed(2)
    },
    getColorCode(deviation) {
      if (isNaN(parseFloat(deviation))) return 'null'
      if (deviation > -10 && deviation < 10) {
        return 'green'
      } else if ((deviation >= -25 && deviation <= -10) || (deviation >= 10 && deviation <= 25)) {
        return 'yellow'
      } else {
        return 'red'
      }
    }
  },
  watch: {
    async watchDataCall() {
      await this.getStringData()
      this.loadStringData()
    }
  }
}
</script>

<style scoped>
.select {
  width: 100%;
  margin-left: 8px;
}

.graph {
  width: 100%;
  height: 400px;
}

.color-coding-box {
  margin: auto;
  width: 60px;
  height: 17px;
}

.mean-value>span {
  font-size: 1.1rem;
  font-weight: lighter;
  text-align: center;
  color: #eee;
}

.mean-value {
  padding-right: 10px;
  padding-left: 10px;
}
</style>

<style>
.scba-fixed-header {
  max-height: 400px;
  overflow: auto;
  display: flex;
}

.theme--dark .scba-fixed-header th {
  position: sticky;
  top: 0;
  color: #c2c6dc !important;
  background: #222743 !important;
}

.theme--light .scba-fixed-header th {
  position: sticky;
  top: 0;
  color: #626262 !important;
  background: #F8F8F8 !important;
}

.scba-fixed-header .v-table__overflow {
  overflow: auto;
}
</style>
